import { memo } from 'react';

import { type CustomizationMachineTypes } from '../../machine';
import { CustomizationIngredientItem } from '../CustomizationIngredientItem';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationModificationItem = memo((props: Props) => {
  const {
    modification,
    activeModificationsMap,
    activeStepperId,
    notice,
    withMinHeight,
    assetAspectRatioY,
    assetAspectRatioX,
    variation,
    canAddMore,
    isMutedStateEnabled,
    maxPortionsForGroup,
    isSplitPortion,
    size,
    dataSet,
    onIncrement,
    onDecrement,
    onRemove,
    onToggleSplitPortion,
    setActiveStepperId,
    resetStepperTimeout,
  } = props;

  const activeModification = activeModificationsMap.get(modification.id);

  // ─────────────────────────────────────────────────────────────────────

  if (!modification.ingredient) return null;

  const { ingredient, outOfStock } = modification;
  const { id, name, asset, kind } = ingredient;

  const activeIngredientQuantity = activeModification?.quantity ?? 0;
  const isSelected = activeModificationsMap.has(modification.id);
  const isOutOfStock = outOfStock ?? false;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <CustomizationIngredientItem
      key={modification.id}
      id={id}
      kind={kind}
      name={name}
      assetUrl={asset.url}
      assetAspectRatioY={assetAspectRatioY}
      assetAspectRatioX={assetAspectRatioX}
      isOutOfStock={isOutOfStock}
      notice={notice}
      quantity={activeIngredientQuantity}
      canAddMore={canAddMore}
      isSelected={isSelected}
      isSplitPortion={isSplitPortion}
      withMinHeight={withMinHeight}
      activeStepperId={activeStepperId}
      variation={variation}
      isMutedStateEnabled={isMutedStateEnabled}
      allowsOnlySinglePortion={maxPortionsForGroup === 1}
      maxPortionsForGroup={maxPortionsForGroup}
      onIncrement={onIncrement}
      onDecrement={onDecrement}
      onRemove={onRemove}
      onToggleSplitPortion={onToggleSplitPortion}
      setActiveStepperId={setActiveStepperId}
      resetStepperTimeout={resetStepperTimeout}
      size={size}
      dataSet={dataSet}
    />
  );
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  modification: CustomizationMachineTypes.ProductModification;
  activeModificationsMap: CustomizationMachineTypes.ModificationsMap;
  activeStepperId: string | null;
  notice?: string;
  withMinHeight?: boolean;
  assetAspectRatioY?: number;
  assetAspectRatioX?: number;
  variation?: 'card' | 'list-item';
  canAddMore: boolean;
  isMutedStateEnabled?: boolean;
  maxPortionsForGroup: number;
  isSplitPortion?: boolean;
  size?: 'sm' | 'md';
  dataSet?: Record<string, string | number>;
  onIncrement: (ingredientId: string) => void;
  onDecrement: (ingredientId: string) => void;
  onRemove?: (ingredientId: string) => void;
  onToggleSplitPortion?: (ingredientId: string) => void;
  setActiveStepperId: (stepperId: string | null) => void;
  resetStepperTimeout: () => void;
};
